/**
 * [C28] Customer entry voucher ("وصل قيد", p02; D-147 as placed by D-272): the statement row's voucher button opens the
 * old voucher window (`CustomerVoucherModal`) with the A4 template (`CustomerEntryVoucherA4`) fed by the server — the
 * number is issued with the operation on opening (`view`); print, PDF and WhatsApp are recorded as issues first, and a
 * reprint is marked copy. [C35] The window switches the copy between A4 and 80 mm (D-149, office default per type); PDF /
 * share = the server's PDF of the same template (D-146) — downloaded, or through the device share sheet; WhatsApp = the
 * free link with the old text (D-063) to the account's stored phone in international form (D-150).
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CustomerVoucherModal } from "@faylasuf/ui";
import { CustomerEntryVoucherA4, Voucher80, entryVoucher80, type CustomerEntryVoucherA4Props, type Voucher80Props } from "@faylasuf/print";
import type { CustomerAccountRow, CustomerEntryVoucher } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { useToast } from "../../app/toast.js";
import { pendingTab, useDocuments, usePrintJob, useVoucherFormat, whatsappLink } from "../../app/documents.js";
import { usePrintOffice } from "../../app/printOffice.js";
import { fmt } from "./format.js";

const PRINT_ID = "customer-entry-voucher-print";

export function voucherProps(v: CustomerEntryVoucher, locale: "ar" | "en", t: (k: string, p?: Record<string, string | number>) => string): Omit<CustomerEntryVoucherA4Props, "id"> {
  const at = new Date(v.operation.postedAt);
  const loc = locale === "ar" ? "ar-EG" : "en-GB";
  const unit = (c: string) => (c === "USD" ? " $" : ` ${t("unit.iqd")}`);
  return {
    locale,
    officeName: v.officeName,
    numberLabel: `${v.operator.employeeCode ?? "AC"}-${v.number}`,
    copy: v.copy,
    dateLabel: `${at.toLocaleDateString(loc, { year: "numeric", month: "2-digit", day: "2-digit", timeZone: v.timeZone })}${locale === "ar" ? " م" : ""}`,
    timeLabel: at.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: v.timeZone }),
    subject: v.subject,
    payment: v.payment,
    customerName: v.customer.name,
    accountNumber: v.customer.accountNumber,
    lines: v.lines.map((l) => ({ currency: l.currency, beforeLabel: `${fmt(l.before)}${unit(l.currency)}`, amountLabel: `${fmt(l.amount)}${unit(l.currency)}`, afterLabel: `${fmt(l.after)}${unit(l.currency)}` })),
    note: v.operation.note,
    boxLabel: v.counterparties.length ? v.counterparties.join(" / ") : null,
    operatorCode: v.operator.employeeCode ?? v.operator.username ?? "",
    words: v.lines.map((l) => l.words ?? "").filter((w) => w !== ""), // [C35] D-148 (server)
  };
}

export function EntryVoucherContainer({ account, operationId, onClose }: { account: CustomerAccountRow; operationId: string; onClose: () => void }) {
  const { source, t, locale } = useApp();
  const { sessionLost } = useSession();
  const toast = useToast();
  const office = usePrintOffice();
  const docs = useDocuments();
  const job = usePrintJob();
  const [format, setFormat, copyIssued] = useVoucherFormat("customer_entry"); // [C36] T-18: the switch is for one copy
  const view = useQuery({ queryKey: ["customer-voucher", account.id, operationId], queryFn: () => source.customerVoucher(account.id, operationId, "view"), staleTime: 0, gcTime: 0, retry: false });
  const [issued, setIssued] = useState<CustomerEntryVoucher | null>(null);
  const [busy, setBusy] = useState(false);
  const fail = (e: unknown) => (isSessionLost(e) ? sessionLost() : toast(errorMessage(t, e), "error"));
  useEffect(() => {
    if (view.error) {
      fail(view.error);
      onClose();
    }
  }, [view.error]);

  const issue = async (channel: "print" | "pdf" | "whatsapp"): Promise<CustomerEntryVoucher | null> => {
    setBusy(true);
    try {
      const v = await source.customerVoucher(account.id, operationId, channel);
      setIssued(v);
      return v;
    } catch (e) {
      fail(e);
      return null;
    } finally {
      setBusy(false);
    }
  };

  // [C35] D-149: the same props print on A4 or on 80 mm; D-146: the PDF is the server's rendering of the same template
  const docOf = (v: CustomerEntryVoucher) => {
    const p = { ...voucherProps(v, locale, t), office };
    if (format === "80mm") return { template: "voucher_80mm" as const, props: entryVoucher80(p, office, printedNow(v.timeZone, locale)) };
    return { template: "customer_entry_a4" as const, props: p };
  };
  const pdfReq = (v: CustomerEntryVoucher) => {
    const d = docOf(v);
    return { template: d.template, props: d.props as unknown as Record<string, unknown>, fileName: `voucher-${v.number}`, docType: "customer_entry", number: String(v.number), operationId, accountId: account.id };
  };
  const print = async () => {
    const v = await issue("print");
    if (!v) return;
    const d = docOf(v);
    job.start(d.template === "voucher_80mm" ? <Voucher80 {...(d.props as Voucher80Props)} id={PRINT_ID} /> : <CustomerEntryVoucherA4 {...(d.props as CustomerEntryVoucherA4Props)} id={PRINT_ID} />, PRINT_ID, format);
    copyIssued();
  };
  const pdf = async (share: boolean) => {
    const v = await issue("pdf");
    if (!v) return;
    setBusy(true);
    try {
      const req = pdfReq(v);
      copyIssued();
      await (share ? docs.sharePdf(req) : docs.downloadPdf(req));
    } finally {
      setBusy(false);
    }
  };
  const whatsapp = async () => {
    const tab = pendingTab(); // [C35 review 9] opened inside the click
    const v = await issue("whatsapp");
    if (!v) return tab.cancel();
    const p = voucherProps(v, locale, t);
    const first = p.lines[0];
    const text = t("print.entry.whatsapp_text", {
      office: v.officeName,
      number: p.numberLabel,
      customer: v.customer.name,
      kind: v.payment ? t("print.entry.whatsapp_kind_payment") : t("print.entry.whatsapp_kind_add"),
      amount: first ? first.amountLabel.trim() : "",
      date: p.dateLabel,
      note: v.operation.note ? t("print.entry.whatsapp_note", { note: v.operation.note }) : "",
    });
    // D-150: the account's own stored phone (this row, by id), in international form
    tab.go(whatsappLink(account.phone, text));
  };

  const shown = issued ?? view.data ?? null;
  if (!shown) return null;
  const preview = docOf(shown);
  return (
    <>
      <CustomerVoucherModal busy={busy || job.busy} onClose={onClose} onWhatsApp={() => void whatsapp()} onSharePdf={() => void pdf(true)} onExportPdf={() => void pdf(false)} onPrint={() => void print()} format={format} onFormatChange={setFormat}>
        {preview.template === "voucher_80mm" ? (
          <div className="py-4 flex justify-center"><Voucher80 {...(preview.props as Voucher80Props)} id="customer-entry-voucher-preview" /></div>
        ) : (
          <CustomerEntryVoucherA4 {...(preview.props as CustomerEntryVoucherA4Props)} id="customer-entry-voucher-preview" />
        )}
      </CustomerVoucherModal>
      {job.portal}
    </>
  );
}

/** D-146: the print time in the office time zone, printed under the operation date. */
export function printedNow(timeZone: string, locale: "ar" | "en"): string {
  const now = new Date();
  const loc = locale === "ar" ? "ar-EG" : "en-GB";
  return `${now.toLocaleDateString(loc, { year: "numeric", month: "2-digit", day: "2-digit", timeZone })} ${now.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit", hour12: true, timeZone })}`;
}
